import { Bullets, Clause, DraftNotice, LegalFooterNav, LegalHero } from "@/components/Legal";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <LegalHero
        testId="terms-hero"
        eyebrow="Legal"
        title="Terms of Service"
        intro="These Terms govern the use of ADIT Connect, the industrial specification, sourcing and
          RFQ platform operated by ADIT International. They explain what the platform does, what it
          does not do, and who carries responsibility for each part of an industrial transaction."
      />

      <div className="mx-auto max-w-[900px] space-y-8 px-5 py-12">
        <DraftNotice />

        <Clause n="1" title="Definitions and acceptance">
          <p>
            <strong>ADIT Connect</strong> (the &ldquo;Platform&rdquo;) is operated by ADIT
            International (&ldquo;ADIT&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).
            A <strong>Buyer</strong> is any entity that searches specifications or submits a request
            for quotation. A <strong>Seller</strong> is any manufacturer, authorised distributor,
            stockist, trader or supplier that registers to receive enquiries and submit quotations.
            A <strong>User</strong> is any person accessing the Platform.
          </p>
          <p>
            By creating an account, submitting an RFQ, submitting a quotation or otherwise using the
            Platform, you accept these Terms on behalf of the entity you represent, and you confirm
            you are authorised to do so. The Platform is intended for business (B2B) use by
            commercial entities only and is not offered to consumers.
          </p>
        </Clause>

        <Clause n="2" title="Role of the Platform">
          <p>
            ADIT Connect normally operates as a <strong>B2B industrial specification, sourcing and
            RFQ platform</strong> that connects Buyers with independent manufacturers, distributors,
            stockists and suppliers. Our function is to normalise technical data, identify candidate
            and equivalent products, route enquiries to relevant Sellers, and present technical and
            commercial offers in a comparable format.
          </p>
          <p>
            Except where ADIT has expressly accepted a specific transaction under clause 4
            (Managed by ADIT), <strong>ADIT Connect is not automatically the manufacturer, seller,
            importer, exporter, technical guarantor or delivery guarantor</strong> of any product
            listed, searched, quoted or ordered through the Platform. We do not take title to goods,
            do not warrant the goods of any Seller, and are not a party to the underlying supply
            contract.
          </p>
          <p>
            Registration of a Seller, verification of its documents, or the appearance of a product
            or brand on the Platform is not an endorsement, certification or recommendation by ADIT,
            and is not a representation that any Seller is an authorised distributor of any brand.
          </p>
        </Clause>

        <Clause n="3" title="Direct Purchase">
          <p>
            Where a Buyer awards an enquiry as a <strong>Direct Purchase</strong>, the underlying
            commercial transaction is concluded <strong>between the Buyer and the Seller</strong>.
            ADIT&apos;s role ends with the introduction and the provision of the platform tools. In
            a Direct Purchase, the Seller is solely responsible for:
          </p>
          <Bullets
            items={[
              "the accuracy of the product it offers, including its description, origin and condition;",
              "the correctness of the product code, part number, model and revision quoted;",
              "technical conformity of the supplied goods to the specification it has accepted;",
              "the authenticity, validity and applicability of any certificate or document it provides, including test certificates, material certificates, calibration records, datasheets and certificates of origin;",
              "its delivery commitments, including lead time, quantity, packing, Incoterm and delivery location;",
              "the warranty it offers on the supplied goods;",
              "any applicable returns, rejections, repairs or replacements; and",
              "its own commercial terms, including price validity, payment terms, taxes, charges and any terms printed on its quotation or invoice.",
            ]}
          />
          <p>
            Any dispute concerning the goods, their conformity, documentation, delivery, payment or
            warranty in a Direct Purchase is a matter between the Buyer and the Seller. ADIT may, at
            its sole discretion and without obligation, assist informally or act on platform
            conduct under clause 16.
          </p>
        </Clause>

        <Clause n="4" title="Managed by ADIT">
          <p>
            Selecting <strong>&ldquo;Managed by ADIT&rdquo;</strong> on an award{" "}
            <strong>only submits a request</strong> for ADIT to consider handling the procurement.
          </p>
          <p>
            Submitting such a request does <strong>not</strong> cause ADIT to become the seller,
            does <strong>not</strong> constitute acceptance of an order, does{" "}
            <strong>not</strong> create any supply, delivery or payment obligation on ADIT, and does{" "}
            <strong>not</strong> transfer any risk or responsibility to ADIT.
          </p>
          <p>
            A <strong>Managed Procurement transaction exists only when ADIT has separately reviewed
            the specific transaction and expressly accepted it</strong>, and has issued or accepted
            the appropriate commercial documentation for that transaction (such as an ADIT
            quotation, sales order acknowledgement, proforma invoice or purchase order). Until that
            point, no Managed Procurement contract exists on any terms, whatever the status shown in
            the Platform interface.
          </p>
          <p>
            Where ADIT does expressly accept a Managed Procurement transaction, the terms of that
            specific transaction are governed by the commercial documentation issued for it, which
            prevails over these Terms in respect of that transaction.
          </p>
        </Clause>

        <Clause n="5" title="Specification and technical information">
          <p>
            Product information, cross-references, equivalent suggestions, technical comparisons,
            parameter tables and specification data on the Platform are provided as{" "}
            <strong>sourcing and procurement assistance only</strong>. They are compiled from
            supplier submissions, published manufacturer literature and ADIT&apos;s own data
            normalisation, and may be incomplete, superseded or contain errors.
          </p>
          <p>
            <strong>Users remain responsible for confirming that any product is suitable for their
            specific application</strong>, including its service conditions, medium, pressure and
            temperature rating, materials, sizing, certification requirements, statutory approvals
            and interaction with other equipment.
          </p>
          <p>
            <strong>Technical equivalence indicated on the Platform must not be treated as an
            engineering guarantee</strong>, a certification of interchangeability, an approval for
            substitution, or professional engineering advice. Where a decision matters, the
            manufacturer-controlled technical document and the authoritative certificate source must
            be consulted, and the Buyer&apos;s own engineering authority must approve the selection.
          </p>
          <p>
            Where the Platform marks a parameter as a{" "}
            <strong>technical deviation</strong>, or as not meeting the stated requirement, the
            Buyer must review and consider that deviation <strong>before making an award</strong>.
            Proceeding to award with a displayed deviation is the Buyer&apos;s acceptance of that
            deviation for its own account.
          </p>
          <p>
            Landed cost figures, duty rates, freight amounts and clearance charges shown on the
            Platform are <strong>estimates for comparison purposes</strong> and are not a quotation
            of the final cost payable.
          </p>
        </Clause>

        <Clause n="6" title="Seller responsibilities and warranties">
          <p>By registering as a Seller and by submitting each quotation, the Seller warrants that:</p>
          <Bullets
            items={[
              "it is authorised to offer, sell and supply the product and brand it quotes in the destination market, and that it will provide evidence of authorisation on request;",
              "all technical and commercial information in its quotation is accurate, current and not misleading;",
              "every document it uploads is genuine, unaltered, lawfully obtained and applicable to the specific product being offered;",
              "the offered goods are new and legitimately sourced unless it has expressly stated otherwise in the quotation;",
              "it will honour the delivery, warranty, price validity and commercial obligations it has stated; and",
              "it complies with all applicable laws and regulations, including trade licensing, customs, sanctions, export control and anti-bribery requirements.",
            ]}
          />
          <p>
            Offering counterfeit, refurbished-as-new, unauthorised or falsely documented goods, or
            uploading a certificate belonging to a different product or batch, is a material breach
            of these Terms and will result in immediate suspension under clause 16.
          </p>
        </Clause>

        <Clause n="7" title="Buyer responsibilities">
          <p>The Buyer must:</p>
          <Bullets
            items={[
              "provide accurate and complete RFQ information, including specification, quantity, unit, delivery location, required dates, Incoterm and certificate requirements;",
              "review each quotation received, including its technical parameters, stated deviations, exclusions and commercial conditions;",
              "confirm the suitability and acceptability of the product for its application before making an award;",
              "comply with the payment terms and commercial terms it agrees with the Seller, or with ADIT under an accepted Managed Procurement transaction; and",
              "not misuse Seller information or Platform data, including supplier identities, pricing, quotations, contact details and technical data.",
            ]}
          />
        </Clause>

        <Clause n="8" title="Non-circumvention">
          <p>
            Where ADIT introduces a Buyer and a Seller to each other through the Platform, and that
            introduction was not already an established, documented trading relationship between
            them before the introduction, each party agrees not to structure a transaction
            deliberately to avoid the Platform, or to avoid fees properly due to ADIT, in respect of
            the product enquiry that was introduced. This applies for a{" "}
            <em>[for counsel: period to be fixed, e.g. twelve (12) months]</em> from the
            introduction.
          </p>
          <p>
            This provision is intended to be commercially reasonable and limited in scope. It does
            not prevent a Buyer or Seller from dealing with any counterparty it already knew, from
            dealing in unrelated products, or from choosing not to transact at all. It does not
            create exclusivity, does not oblige either party to transact, and does not restrict
            either party&apos;s ordinary business.
          </p>
          <p>
            <em>
              [for counsel] The duration, scope, remedy and enforceability of this clause, and any
              associated fee, must be settled by UAE legal counsel before commercial launch.
            </em>
          </p>
        </Clause>

        <Clause n="9" title="Supplier subscriptions">
          <p>
            Sellers may access the Platform on the <strong>Founding (free)</strong> plan or on a paid{" "}
            <strong>Pro</strong> or <strong>Enterprise</strong> subscription. The Founding plan
            provides limited access, including a capped number of live enquiries. Pro and Enterprise
            provide expanded access; Enterprise additionally provides team accounts for multiple
            users of the same company.
          </p>
          <Bullets
            items={[
              "Billing frequency: paid subscriptions are billed annually in advance in the currency shown at checkout, unless otherwise agreed in writing.",
              "Renewal: subscriptions renew automatically for a further term unless cancelled before the renewal date. The renewal is charged to the payment method on file.",
              "Cancellation: a Seller may cancel at any time; cancellation takes effect at the end of the paid term and the Seller retains paid access until then. Part-term cancellation does not create a pro-rata refund unless required by law.",
              "Failed or cancelled payment: if a payment fails, is reversed, refunded or disputed, or if a subscription is cancelled, the account reverts to Founding (free) limits and paid features and enquiry allowances are withdrawn accordingly.",
              "Price changes apply from the next renewal and will be notified in advance.",
            ]}
          />
          <p>
            <strong>
              A subscription provides access to platform services and to sourcing opportunities. It
              does not guarantee any particular number of RFQs, matched enquiries, quotations,
              awards, orders, revenue or sales, and no such outcome is promised or implied.
            </strong>{" "}
            Enquiry volume depends on Buyer demand, the Seller&apos;s registered categories, brands
            and markets, the completeness of its product data, and its own responsiveness and
            competitiveness.
          </p>
          <p>
            Subscription fees are non-refundable except where expressly stated by ADIT in writing or
            where a refund is required by applicable law.
          </p>
        </Clause>

        <Clause n="10" title="Payments and Stripe">
          <p>
            Subscription payments are processed by <strong>Stripe</strong>, a third-party payment
            provider. By subscribing you also accept Stripe&apos;s applicable terms, and your
            payment information is collected and handled by Stripe under the payment-provider
            arrangements applicable to that service.
          </p>
          <p>
            <strong>ADIT Connect does not receive or store your complete card number, expiry date or
            security code.</strong> We retain only the transaction records, subscription status and
            tax-invoice data necessary to operate and account for your subscription.
          </p>
          <p>
            Tax invoices issued through the Platform reflect the amounts charged and any applicable
            VAT. Where a payment is later reversed, refunded or successfully disputed, ADIT may
            adjust the account&apos;s access accordingly and recover any amounts due.
          </p>
        </Clause>

        <Clause n="11" title="User content and uploaded documents">
          <p>
            Users upload content to the Platform, including RFQs, drawings, bills of quantity,
            datasheets, test and material certificates, quotations, price lists, product data,
            shipping documents and company records.
          </p>
          <p>
            You must have the right to upload and share everything you submit. You must not upload
            content that infringes a third party&apos;s intellectual property or confidentiality
            rights, that you are contractually prohibited from disclosing, that contains malware, or
            that is false or altered.
          </p>
          <p>
            You retain ownership of the content you upload. You grant ADIT a non-exclusive licence
            to host, process, transmit, index and display that content strictly as necessary to
            operate the Platform and provide the service to you — for example, showing your RFQ to
            matched Sellers, showing your quotation to the relevant Buyer, and generating comparison
            documents and invoices.
          </p>
          <p>
            ADIT may additionally use normalised, aggregated technical product attributes to improve
            the specification and equivalence database. ADIT will not publish your commercially
            sensitive pricing, quotation values or customer identities as your data.
          </p>
        </Clause>

        <Clause n="12" title="Confidentiality">
          <p>
            RFQ and quotation information is commercially sensitive. It is intended to be accessible
            only to authorised users in accordance with the Platform&apos;s permission model: a
            Buyer sees the quotations submitted to its own enquiries; a Seller sees the enquiries
            matched to it and its own quotations; a Seller does not see the prices or contents of
            competing quotations; and ADIT staff may access data as necessary for platform
            operation, verification, support and dispute handling.
          </p>
          <p>
            Each User must keep confidential the commercial information of other Users that it
            receives through the Platform, must use it only for the purpose of the enquiry to which
            it relates, and must not disclose or resell it. Account credentials must not be shared.
          </p>
        </Clause>

        <Clause n="13" title="Intellectual property">
          <p>
            ADIT retains all rights in the ADIT and ADIT Connect names, logos and branding, the
            design and user interface of the Platform, the structure and schema of the product and
            specification database, the compilation and normalisation of cross-reference and
            equivalence data, the comparison logic, the software, and the written content of the
            website.
          </p>
          <p>
            Nothing on the Platform transfers any right in the trademarks, catalogues, part
            numbering systems, drawings or technical literature of any third-party manufacturer.
            Such marks and materials remain the property of their respective owners and are
            referenced only for identification and interoperability purposes in an industrial
            sourcing context.
          </p>
          <p>
            If you believe content on the Platform infringes your rights, contact us at
            projects@aditintl.com and we will review it promptly.
          </p>
        </Clause>

        <Clause n="14" title="Prohibited use">
          <p>You must not:</p>
          <Bullets
            items={[
              "use the Platform for fraud, misrepresentation or any unlawful purpose;",
              "scrape, crawl, harvest or systematically extract the product, specification, cross-reference, supplier or pricing database, or use automated tools to do so;",
              "copy or reproduce the database or a substantial part of it, or build a competing dataset or service from it;",
              "introduce malware, attempt to breach security, probe or test the vulnerability of the Platform, or bypass authentication, permissions or rate limits;",
              "share accounts or credentials, or allow access by anyone outside the registered entity;",
              "upload false, altered, expired or misattributed documents or certificates;",
              "impersonate another company, brand, principal or person, or falsely claim brand authorisation;",
              "submit RFQs or quotations that are not genuine, including enquiries made only to extract competitor pricing;",
              "circumvent the Platform contrary to clause 8; or",
              "interfere with the operation of the Platform or with other Users' use of it.",
            ]}
          />
        </Clause>

        <Clause n="15" title="Disclaimers, limitation of liability and indemnity">
          <p>
            The Platform is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis.
            To the maximum extent permitted by applicable law, and except where ADIT has expressly
            accepted a transaction under clause 4, ADIT is not liable for:
          </p>
          <Bullets
            items={[
              "the performance, solvency, conduct or default of any third-party Seller or Buyer;",
              "defects in, or non-conformity of, goods supplied by a Seller;",
              "late, short, damaged or non-delivery by a Seller;",
              "incorrect, incomplete or misleading information provided by a Seller or Buyer, including part numbers and specifications;",
              "third-party certifications, test reports or documents, including forged or misapplied documents;",
              "business interruption, loss of production, loss of profit, loss of contract, loss of opportunity, loss of goodwill or loss of anticipated savings;",
              "any indirect, incidental, special, punitive or consequential loss;",
              "unavailability, downtime, data loss, interruption or degradation of the Platform; and",
              "external websites, manufacturer portals, payment providers, courier services or other third-party services linked to or used with the Platform.",
            ]}
          />
          <p>
            Where ADIT is found liable notwithstanding the above, its total aggregate liability to a
            User for all claims arising in any twelve-month period is limited to{" "}
            <em>
              [for counsel: the greater of the subscription fees paid by that User in the preceding
              twelve (12) months, or a fixed cap to be agreed]
            </em>
            .
          </p>
          <p>
            <strong>
              Nothing in these Terms excludes or limits any liability that cannot lawfully be
              excluded or limited under applicable law, including liability for fraud, fraudulent
              misrepresentation, death or personal injury caused by negligence, or any liability
              that mandatory UAE law prohibits us from excluding.
            </strong>
          </p>
          <p>
            <strong>Indemnity.</strong> You agree to indemnify and hold harmless ADIT, its
            affiliates and their personnel against reasonable losses, damages, liabilities, claims,
            fines and costs (including reasonable legal costs) arising from: your misuse of the
            Platform; false, misleading or unauthorised information or documents you submit; your
            infringement of a third party&apos;s intellectual property or confidentiality rights;
            your breach of these Terms; and your breach of applicable law. This indemnity does not
            apply to the extent the loss was caused by ADIT&apos;s own breach, negligence or
            wilful misconduct.
          </p>
        </Clause>

        <Clause n="16" title="Suspension and termination">
          <p>
            ADIT may suspend or terminate an account, withdraw verified status, remove product data
            or withhold access to enquiries, with immediate effect where reasonably necessary, in
            cases of: suspected fraud or counterfeit goods; abuse of the Platform or of other Users;
            expired, false or misattributed documents or certificates; non-payment or a reversed
            payment; circumvention under clause 8; unauthorised data extraction; or any other
            material breach of these Terms.
          </p>
          <p>
            Where circumstances allow, ADIT will give notice and an opportunity to correct the
            issue. On termination, access to the Platform ends; obligations already incurred toward
            a counterparty, and clauses 8, 11, 12, 13 and 15, survive. ADIT may retain records as
            required by law and for legitimate business purposes as described in the Privacy Policy.
          </p>
        </Clause>

        <Clause n="17" title="Changes to the Platform and to these Terms">
          <p>
            ADIT may develop, change or discontinue features of the Platform. We may amend these
            Terms; material changes will be notified by email or by notice in the Platform, and the
            &ldquo;last updated&rdquo; date above will change. Continued use after a change takes
            effect constitutes acceptance. If you do not accept a change, you must stop using the
            Platform and may cancel your subscription in accordance with clause 9.
          </p>
        </Clause>

        <Clause n="18" title="Governing law and disputes">
          <p>
            <em>
              [for counsel — placeholder to be finalised by qualified UAE legal counsel before
              commercial launch]
            </em>
          </p>
          <p>
            It is intended that these Terms be governed by the laws of the United Arab Emirates, as
            applied in the relevant Emirate or financial free zone, and that the courts or arbitral
            forum of that jurisdiction have exclusive jurisdiction over disputes arising out of or
            in connection with them. The following must be confirmed by counsel: the governing
            jurisdiction (for example, Emirate of Dubai onshore, DIFC or ADGM); whether disputes go
            to court or to arbitration (and if arbitration, the seat, rules, language and number of
            arbitrators); the position of free-zone entities; and the enforceability in the UAE of
            clauses 8, 15 and 16 as drafted.
          </p>
          <p>
            Nothing in this clause prevents either party from seeking urgent injunctive relief in
            any competent jurisdiction to protect confidential information or intellectual property.
          </p>
        </Clause>

        <Clause n="19" title="General">
          <p>
            These Terms, together with the Privacy Policy and any commercial documentation issued
            for an accepted Managed Procurement transaction, form the entire agreement between you
            and ADIT in respect of the Platform. If any provision is found unenforceable, the
            remainder continues in force and the unenforceable provision is to be read down to the
            minimum extent necessary. No failure to enforce a right is a waiver of it. You may not
            assign your rights without our written consent. Nothing in these Terms creates a
            partnership, agency, employment or joint venture between the parties.
          </p>
        </Clause>

        <Clause n="20" title="Contact">
          <p>
            Questions about these Terms, or notices under them, should be sent to{" "}
            <a href="mailto:projects@aditintl.com" className="font-medium text-[#147D78] hover:underline">
              projects@aditintl.com
            </a>
            , or submitted through the Get help page.
          </p>
        </Clause>

        <LegalFooterNav current="/terms" />
      </div>
    </div>
  );
}
